/**
 * Turning a clicked cell into the report underneath it.
 *
 * One drill, one rule (D-20): whatever was clicked, the whole coordinate
 * travels as filters — every row group, every column group, plus the report's
 * own filters and period. A ratio cell and a plain number cell drill
 * identically, and a time group is just another group, so neither needs a
 * special case.
 *
 * Two things vary, and neither changes the operation:
 *
 *  - HOW MUCH coordinate the click carries. A data cell knows both axes, a
 *    row-total knows only the rows, the grand total knows neither. The caller
 *    decides that by what it puts in the coordinate — {@link cellCoordinate}.
 *  - WHERE it lands. The model's own drill — the rows behind the figure — or a
 *    saved report whose LAYOUT the reader would rather read them in. Which
 *    saved reports qualify is `drill-targets.ts`; how the landing is composed
 *    is {@link landingSpec}, and it is the same for both.
 *
 * LAYOUT FROM THE TARGET, QUESTION FROM THE SOURCE. The landing report brings
 * its model, kind, axes, column order, sort and formatting. The clicked cell
 * brings the filters and the period. The target's OWN filters and period are
 * dropped: keeping them shows a subset of the rows behind the number, and a
 * drill that cannot tie out to the figure it came from is not a drill.
 *
 * See docs/reporting/requirements.md §FR-5 and docs/reporting/drill-down.md.
 */
import { type FilterNode, type FilterRule, isFilterGroup } from './filter';
import {
  canFilterField,
  canList,
  type DrillTarget,
  findDimension,
  findMeasure,
  type Measure,
  type MeasureSource,
  type ReportModel,
} from './model';
import type { DimTuple } from './result';
import { type ReportSpec, reportSpecSchema } from './spec';

/**
 * Where the user clicked: dimension id → the key at that cell.
 *
 * Keys, never labels — two people can share a name (FR-1.7). A `null` is the
 * cell for "no value" — entries with no customer, an unclassified country —
 * and it travels as an `is blank` rule. Dropping it would widen the landing to
 * every value of that dimension, and the rows would no longer add up to the
 * number that was clicked.
 */
export type DrillCoordinate = Record<string, string | null>;

/** A resolved drill, ready to become the target's spec or its URL. */
export interface DrillRequest {
  /** The report to open — the current one when the target named none. */
  report: string;
  /** What to group the landing report by, when the target chose. */
  dimension?: string;
  /** The landing report's filters, named as the TARGET calls them. */
  filters: FilterNode[];
  from: string;
  to: string;
}

/**
 * The coordinate one pivot cell pins.
 *
 * `rowKey` is the row's tuple — the whole of it for a leaf, a prefix for a
 * subtotal or a folded group, absent for the bottom total. `colKey` is the
 * column's, absent in the total band. Each is aligned with the start of its
 * axis, so a prefix pins the outer levels and leaves the inner ones free.
 *
 * BOTH AXES, always (D-20). Clicking a Billable cell pins the row's customer
 * AND `billable`; carrying the rows alone lands on billable and non-billable
 * hours together, and the next menu cannot narrow on what the reader chose.
 */
export function cellCoordinate(
  spec: Pick<ReportSpec, 'rows' | 'cols'>,
  rowKey: DimTuple | undefined,
  colKey: DimTuple | undefined,
): DrillCoordinate {
  const at: DrillCoordinate = {};
  const pin = (dims: readonly string[], tuple: DimTuple | undefined) => {
    for (const [i, value] of (tuple ?? []).entries()) {
      const id = dims[i];
      if (id) at[id] = value.key;
    }
  };
  pin(spec.rows, rowKey);
  pin(spec.cols, colKey);
  return at;
}

/** The coordinate as filter rules, one per pinned dimension. */
function coordinateRules(
  at: DrillCoordinate,
  rename: (dimensionId: string) => string,
): FilterRule[] {
  return Object.entries(at).map(([dimensionId, key]) => ({
    // Named for the dimension that was CLICKED, not for what the target
    // calls it: a `map` may send two dimensions to one field, and two rules
    // sharing an id is a spec `validateFilters` refuses — so the drill would
    // have opened a refused report instead of the rows underneath.
    id: `drill-${dimensionId}`,
    field: rename(dimensionId),
    ...(key === null
      ? { op: 'blank' as const }
      : { op: 'in' as const, value: { kind: 'list' as const, values: [key] } }),
    paused: false,
  }));
}

/**
 * The landing's filters: the source's own, with the coordinate ANDed on top.
 *
 * The coordinate goes on the TOP level, and a group is carried whole. Anything
 * else would widen the filter the drill came out of: a coordinate pushed inside
 * an `or` group becomes another way to satisfy it, so clicking one customer's
 * cell would return every row the group already matched.
 *
 * Where the coordinate and a top-level rule name the same dimension the
 * coordinate wins — it is the narrower of the two, and it is what the reader
 * actually pointed at. A rule inside a group is left alone: replacing one arm
 * of an OR would rewrite a question nobody asked.
 */
export function drillFilters(
  spec: Pick<ReportSpec, 'filters'>,
  at: DrillCoordinate,
  rename: (dimensionId: string) => string = (id) => id,
): FilterNode[] {
  const coordinate = coordinateRules(at, rename);
  const replaced = new Set(coordinate.map((rule) => rule.field));
  const carried = spec.filters.map((node) =>
    isFilterGroup(node)
      ? { ...node, children: node.children.map((r) => ({ ...r, field: rename(r.field) })) }
      : { ...node, field: rename(node.field) },
  );
  return [
    ...carried.filter((node) => isFilterGroup(node) || !replaced.has(node.field)),
    ...coordinate,
  ];
}

/**
 * Compose the drill a click produces.
 *
 * `from` is the dimension whose header was clicked, if one was; a data cell
 * passes nothing and gets the model's target. Returns `null` when neither names
 * one, which is how a caller knows not to offer the affordance at all.
 */
export function drillRequest(
  model: ReportModel,
  spec: ReportSpec,
  at: DrillCoordinate,
  from?: string,
): DrillRequest | null {
  const target = (from ? findDimension(model, from)?.drillTo : undefined) ?? model.drillTo;
  if (!target) return null;
  return {
    report: target.report ?? spec.model,
    ...(target.dimension ? { dimension: target.dimension } : {}),
    filters: drillFilters(spec, at, renamer(target)),
    from: spec.from,
    to: spec.to,
  };
}

/** Names pass through unchanged unless the target calls them something else. */
const renamer = (target: DrillTarget) => (dimensionId: string) =>
  target.map?.[dimensionId] ?? dimensionId;

/** Does this model offer a drill at all? `budgets` does not, on purpose. */
export const isDrillable = (model: ReportModel): boolean => !!model.drillTo;

/**
 * The measure an ENTRY carries for this figure, or `null` if no entry carries
 * any of it.
 *
 * A summed measure carries itself. A RATIO carries its numerator — the rows
 * behind "62.4% utilized" are the utilized hours, and the capacity they are
 * divided by is not a fact any entry records. Available Hours carries nothing:
 * it is working days × 8, per person, so there is no column to list and
 * nothing to keep non-zero.
 *
 * Works on both halves: `canList` reads `rowSql` on the server and the
 * catalog's `listable` in the browser.
 */
export function drillBasis(model: Pick<ReportModel, 'measures'>, measureId: string): string | null {
  const m = findMeasure(model, measureId);
  if (!m) return null;
  if (canList(model, m)) return m.id;
  if (m.agg === 'ratio' && m.numerator) {
    const numerator = findMeasure(model, m.numerator);
    if (numerator && canList(model, numerator)) return numerator.id;
  }
  return null;
}

/**
 * `landing`, with the basis of `measureId` lent from `source` when it lacks it.
 *
 * WHAT MAKES THIS SAFE is not here — it is the server's check that the two
 * models read the same facts, so the lent expression means the same thing on
 * both (see `AnalyticsService`). This only moves the definition: the measure,
 * and the measures it is composed of, so a composed measure still resolves its
 * row-level form against the model it now sits in.
 */
export function lendBasis<M extends Pick<ReportModel, 'measures'>>(
  landing: M,
  source: Pick<ReportModel, 'measures'>,
  measureId: string,
): M {
  const basis = drillBasis(source, measureId);
  if (!basis || findMeasure(landing, basis)) return landing;
  const lent: Measure[] = [];
  const visit = (id: string) => {
    if (lent.some((m) => m.id === id) || findMeasure(landing, id)) return;
    const m = findMeasure(source, id);
    if (!m) return;
    lent.push(m);
    for (const part of (m as MeasureSource).parts ?? []) visit(part);
  };
  visit(basis);
  return { ...landing, measures: [...landing.measures, ...lent] };
}

/** Where the reader chose to land. */
export type DrillDestination =
  /** The model's own drill: the rows behind the figure. */
  | { kind: 'entries' }
  /** A saved report, read for its layout only. */
  | { kind: 'report'; spec: ReportSpec };

export interface DrillLanding {
  /** Runnable as-is. */
  spec: ReportSpec;
  /**
   * Coordinate dimensions NOT carried, because the landing cannot filter on
   * them. Each one is an attribute of something that did travel — eligibility
   * refuses any other — so the rows are the same; the tray can still say so.
   */
  dropped: string[];
  /** The target's own filters, by dimension, which did not apply here. */
  ignoredFilters: string[];
  /** The measure added so the clicked figure appears on the landing. */
  appended?: string;
}

/**
 * The spec a drill lands on.
 *
 * `measureId` is the measure of the cell clicked, on the SOURCE's model. It is
 * what makes the landing about the number rather than about the cell: its
 * basis becomes a column (or a measure) and, on a listing, the condition that
 * a row carry some of it. See `spec.basis`.
 *
 * Returns `null` when the landing's model is not one the caller may read.
 */
export function landingSpec(
  models: readonly ReportModel[],
  source: { model: ReportModel; spec: ReportSpec },
  at: DrillCoordinate,
  measureId: string,
  to: DrillDestination,
): DrillLanding | null {
  const target = to.kind === 'entries' ? source.model.drillTo : undefined;
  if (to.kind === 'entries' && !target) return null;
  const modelId = to.kind === 'entries' ? (target?.report ?? source.spec.model) : to.spec.model;
  const landing = models.find((m) => m.id === modelId);
  if (!landing) return null;

  // Only the top-level coordinate rules may be dropped, and only because the
  // landing cannot express them. A GROUP is never split — see `drillFilters`.
  // A MEASURE filter carries like a dimension's (`Hours > 8`): dropping it
  // would list rows the figure never counted.
  const lentLanding = lendBasis(landing, source.model, measureId);
  const filterable = (field: string) => canFilterField(lentLanding, field);
  const dropped: string[] = [];
  const filters = drillFilters(source.spec, at, target ? renamer(target) : undefined).filter(
    (node) => {
      if (isFilterGroup(node)) return node.children.every((r) => filterable(r.field));
      if (filterable(node.field)) return true;
      dropped.push(node.field);
      return false;
    },
  );

  const basis = drillBasis(source.model, measureId);
  const lent = lentLanding;
  // The clicked measure itself when the landing offers it — a Role Mismatch
  // report can show Overall Utilization %, and that is the figure clicked.
  // Otherwise its basis, which is what an entry can carry.
  const shown =
    findMeasure(landing, measureId) && to.kind === 'report' && to.spec.kind === 'pivot'
      ? measureId
      : basis;
  const question = {
    filters,
    from: source.spec.from,
    to: source.spec.to,
    ...(source.spec.period ? { period: source.spec.period } : {}),
  };
  // The model that DEFINES the clicked measure. A landing that was lent one —
  // Client Billable Hours as a column on timesheets — does not define it, so
  // clicking it again has to name the model it was lent from: a basis says
  // where the expression lives, and the server looks it up there.
  const native = models.find((m) => m.id === source.model.id);
  const owner =
    native && !findMeasure(native, measureId) && source.spec.basis
      ? source.spec.basis.model
      : source.model.id;
  const basisField = (kind: ReportSpec['kind']) =>
    basis && (kind === 'detail' || !findMeasure(landing, shown ?? ''))
      ? { basis: { model: owner, measure: measureId } }
      : {};
  // The report the chain STARTED on, which every level below reads on behalf
  // of — see `spec.origin`. A source that is itself a landing already carries
  // it, so it is passed on rather than replaced by the landing's own model;
  // otherwise the source is where the chain starts. Set whatever was clicked:
  // unlike the basis, it does not depend on the figure.
  const origin = source.spec.origin ?? source.model.id;

  if (to.kind === 'entries') {
    // A dimension pinned to one value prints the same word on every row, and
    // the tray names it among the filters carried in. Pinned ANYWHERE up the
    // chain, not only by this click: an earlier level's pin arrives as a
    // single-valued rule, and is just as constant down here.
    const pinned = new Set([
      ...Object.keys(at).filter((id) => at[id] !== null),
      ...filters.flatMap((node) =>
        !isFilterGroup(node) &&
        node.op === 'in' &&
        node.value?.kind === 'list' &&
        node.value.values.length === 1
          ? [node.field]
          : [],
      ),
    ]);
    const columns = (target?.columns ?? []).filter((id) => {
      if (pinned.has(id)) return false;
      const d = findDimension(landing, id);
      if (d) return true;
      const m = findMeasure(lent, id);
      return !!m && canList(lent, m);
    });
    if (basis && !columns.includes(basis)) columns.push(basis);
    return {
      spec: reportSpecSchema.parse({
        model: landing.id,
        kind: 'detail',
        ...question,
        columns,
        // In the order they were worked — when the date is a column at all. A
        // click that pinned the day leaves it out, and a listing may only sort
        // by what it lists.
        ...(columns.includes('date') ? { rowSort: [{ by: 'date', dir: 'asc' }] } : {}),
        // The total row IS the tie-out: the figure clicked, summed back up
        // over the rows it came from.
        totals: { row: true, col: true },
        ...basisField('detail'),
        origin,
      }),
      dropped,
      ignoredFilters: [],
      ...(basis ? { appended: basis } : {}),
    };
  }

  const own = to.spec;
  const ignoredFilters = [
    ...new Set(
      own.filters.flatMap((node) =>
        isFilterGroup(node) ? node.children.map((r) => r.field) : [node.field],
      ),
    ),
  ];
  let appended: string | undefined;
  const layout: Partial<ReportSpec> = {};
  if (own.kind === 'pivot' && shown && !own.measures.includes(shown)) {
    layout.measures = [...own.measures, shown];
    appended = shown;
  }
  if (own.kind === 'detail' && basis && !own.columns.includes(basis)) {
    layout.columns = [...own.columns, basis];
    appended = basis;
  }
  const { offset: _offset, toggledGroups: _toggled, period: _period, ...kept } = own;
  return {
    spec: reportSpecSchema.parse({
      ...kept,
      ...layout,
      ...question,
      ...basisField(own.kind),
      origin,
    }),
    dropped,
    ignoredFilters,
    ...(appended ? { appended } : {}),
  };
}
